import React from 'react';
import {
  VStack, HStack, Text, Button, ButtonIcon, ButtonText, Input, InputField, Box,
  FormControl, FormControlError, FormControlErrorText,
} from '@gluestack-ui/themed';
import { Plus, Trash2 } from 'lucide-react-native';
import {
  useFormContext,
  useFieldArray,
  Controller,
  FieldErrors,
} from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import GenericDataProvider from '@/common/api/GenericDataProvider';
import { useDialog } from '@/common/context/DialogContext';
import SettingsGroupCard from '@/common/components/forms/SettingsGroupCard';
import type { CourseModule, CourseSegment } from '@/common/api/models';

interface SegmentsEditorProps {
  courseId?: string;
}

const SegmentsEditor: React.FC<SegmentsEditorProps> = ({ courseId }) => {
  const methods = useFormContext();
  const { control } = methods;
  const { showDialog } = useDialog();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'segments',
  });

  // Fetch modules to check if a segment is in use
  const { data: modulesInUse } = useQuery({
    queryKey: ['/courses', courseId, 'modules', 'check-segments'],
    queryFn: async () => {
      if (!courseId) return [];
      const dataProvider = new GenericDataProvider();
      const result = await dataProvider.getLinkedItems('courses', courseId, 'modules', {
        page: 1,
        pageSize: 1000,
        filter: {},
      });
      return (result.data || []) as CourseModule[];
    },
    enabled: !!courseId,
  });

  const isSegmentInUse = (segmentId: string) => (modulesInUse as CourseModule[])?.some((m) => m.courseSettings?.segmentId === segmentId);

  const handleRemove = (index: number) => {
    const segment = fields[index] as unknown as CourseSegment;
    if (segment.id && isSegmentInUse(segment.id)) {
      showDialog({
        title: 'Cannot Remove Segment',
        message: 'This segment is currently used by one or more modules. Please reassign the modules before removing this segment.',
        buttons: [{ text: 'OK', action: 'primary' }],
      });
      return;
    }

    remove(index);
  };

  const handleAdd = () => {
    append({ id: `segment-${Date.now()}`, title: '', sortIndex: fields.length });
  };

  return (
    <SettingsGroupCard title="Course Segments">
      <VStack space="md">
        {fields.length > 0 && (
          <HStack space="md" px="$1">
            <Box flex={1}>
              <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase">
                Segment Title
              </Text>
            </Box>
            <Box w={80}>
              <Text size="xs" fontWeight="$bold" color="$textSubtle" textTransform="uppercase">
                Sort Order
              </Text>
            </Box>
            <Box w={40} />
          </HStack>
        )}

        {fields.map((field, index) => {
          const segmentsErrors = methods.formState.errors.segments as unknown as FieldErrors<CourseSegment>[];
          const error = segmentsErrors?.[index]?.title;
          return (
            <VStack key={field.id} space="xs">
              <HStack space="md" alignItems="flex-start">
                <Box flex={1}>
                  <FormControl isInvalid={!!error}>
                    <Controller
                      control={control}
                      name={`segments.${index}.title` as const}
                      rules={{ required: 'Segment title is required' }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input size="sm">
                          <InputField
                            placeholder="e.g. Foundations"
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                          />
                        </Input>
                      )}
                    />
                    <FormControlError>
                      <FormControlErrorText size="2xs">
                        {error?.message}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                </Box>
                <Box w={80}>
                  <Controller
                    control={control}
                    name={`segments.${index}.sortIndex` as const}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Input size="sm">
                        <InputField
                          placeholder="0"
                          keyboardType="numeric"
                          value={String(value ?? '')}
                          onChangeText={(val) => onChange(val === '' ? undefined : Number(val))}
                          onBlur={onBlur}
                        />
                      </Input>
                    )}
                  />
                </Box>
                <Button
                  size="sm"
                  variant="outline"
                  action="negative"
                  onPress={() => handleRemove(index)}
                  isDisabled={!fields[index]}
                >
                  <ButtonIcon as={Trash2} />
                </Button>
              </HStack>
            </VStack>
          );
        })}

        {fields.length === 0 && (
          <Text size="xs" color="$textSubtle" italic>
            No segments defined. The course will be displayed as a single flat list.
          </Text>
        )}

        <Button size="sm" variant="outline" action="primary" onPress={handleAdd} alignSelf="flex-start" mt="$2">
          <ButtonIcon as={Plus} mr="$2" />
          <ButtonText>Add Segment</ButtonText>
        </Button>
      </VStack>
    </SettingsGroupCard>
  );
};

export default SegmentsEditor;
